<template>
  <div>
    <WorkpackagesToolbar
      doc-path="160-templates"
      :entity="entities.template"
      @on-search="onSearch"
      @on-sort="onSort"
    />

    <div>
      <v-btn
        data-id-e2e="btnNewWorkpackageTemplate"
        primary
        :disabled="isNewButtonDisabled"
        @click="addNewWorkpackage({ isTemplate: true })"
        >{{ $t('templatesPage.addNewButton') }}</v-btn
      >
      <v-btn
        v-if="bundlesEnabled"
        class="ml-1"
        data-id-e2e="btnNewWorkpackageTemplateWithoutBundle"
        primary
        :disabled="isNewButtonDisabled"
        @click="addNewWorkpackage({ isTemplate: true, withoutBundle: true })"
      >
        {{ $t('templatesPage.addNewWithoutBundleButton') }}
      </v-btn>

      <v-row>
        <v-col class="horizontal-divider-container">
          <div class="horizontal-divider" />
        </v-col>
      </v-row>
    </div>

    <v-card class="col-box">
      <v-alert v-if="loading" border="start" variant="tonal" border-color="primary-accent-4">
        <v-progress-circular indeterminate color="primary" size="20" />
        {{ $t('templatesPage.loadingWorkpackageTemplates') }}
      </v-alert>
      <v-row no-gutters>
        <v-col class="workpackages-container colspan">
          <WorkpackagesList
            v-if="sortedWorkpackagesList.length"
            :workpackages="sortedWorkpackagesList"
            :visible-workpackages="visibleTemplates"
            :entity="entities.template"
          />
          <v-alert v-if="!loading && !visibleTemplates.length" type="info" variant="tonal">
            {{ $t('workpackages.noActiveWps') }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card>

    <v-row no-gutters class="my-4">
      <v-col class="horizontal-divider-container colspan">
        <div class="horizontal-divider" />
      </v-col>
    </v-row>

    <v-row align="center" no-gutters>
      <v-col class="colspan">
        <v-btn
          class="me-1"
          data-id-e2e="btnNewWorkpackageTemplate"
          primary
          :disabled="isNewButtonDisabled"
          @click="addNewWorkpackage({ isTemplate: true })"
        >
          {{ $t('templatesPage.addNewButton') }}
        </v-btn>
        <v-btn
          v-if="bundlesEnabled"
          data-id-e2e="btnNewWorkpackageTemplateWithoutBundle"
          primary
          :disabled="isNewButtonDisabled"
          @click="addNewWorkpackage({ isTemplate: true, withoutBundle: true })"
        >
          {{ $t('templatesPage.addNewWithoutBundleButton') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { useDomUtility } from '@/composables/dom-utils';
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';
import { useWorkpackagesStore } from '@/stores/workpackages';
import searchUtils from '@/utils/search-utils';
import sortUtils from '@/utils/sort-utils';
import entities from '@enums/entities';
import { map, size } from 'lodash';
import { mapActions, mapState, mapStores } from 'pinia';

export default {
  setup() {
    const { canCreateWorkpackage } = usePermission();
    const { bundlesEnabled } = useFeature();
    const { scrollToElement } = useDomUtility();
    return {
      canCreateWorkpackage,
      bundlesEnabled,
      scrollToElement,
    };
  },
  data() {
    return {
      sortOrder: null,
      searchText: '',
      entities,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, ['loadingWorkpackageTemplates', 'workpackageTemplates']),
    ...mapStores(useWorkpackagesStore, useSnackbarStore, useWorkpackageProductsStore),
    workpackageTemplates() {
      return this.workpackageTemplates;
    },
    loading() {
      return this.loadingWorkpackageTemplates;
    },

    isNewButtonDisabled() {
      // return false;
      const templates = size(this.workpackageTemplates);
      return (
        !this.canCreateWorkpackage || !!(templates && !this.workpackageTemplates[templates - 1]._id)
      );
    },

    visibleTemplates() {
      return searchUtils.getResultsByNameDescription(
        this.searchText,
        this.workpackagesStore.workpackageTemplates,
        this.sortOrder
      );
    },

    sortedWorkpackagesList() {
      return this.sortOrder
        ? sortUtils.sortItemList(this.workpackagesStore.workpackageTemplates, this.sortOrder)
        : this.workpackagesStore.workpackageTemplates;
    },
  },
  // Initialize getting all workpackages
  async created() {
    await this.fetchWorkpackages({ shouldGetTemplates: true, pickExtra: ['status'] });

    // // Collect all the workpackage scenarios that are likely to be required for the user.
    await this.fetchScenarios({
      params: { where: { workpackageId: { $in: map(this.workpackageTemplates, '_id') } } },
    });
  },

  methods: {
    ...mapActions(useWorkpackagesStore, [
      'fetchWorkpackages',
      'addWorkpackage',
      'setLoadingWorkpackageTemplates',
    ]),
    ...mapActions(useScenariosStore, ['fetchScenarios']),

    async addNewWorkpackage(options) {
      const addedWorkpackage = await this.addWorkpackage(options);
      this.scrollToElement(`wp-list-element-${addedWorkpackage._id}`);
    },

    onSearch(searchText) {
      this.setLoadingWorkpackageTemplates(true);
      this.searchText = searchText;
      if (!searchText) {
        this.setLoadingWorkpackageTemplates(false);
        return;
      }
      this.setLoadingWorkpackageTemplates(false);
    },

    onSort(sortOrder) {
      this.sortOrder = sortOrder;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-application .headline) {
  font-family: $assortment-regular-font;
}

:deep(.progress-bar) {
  padding-bottom: 10em;
}
</style>
